import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { History, RotateCcw, User as UserIcon } from 'lucide-react';
import { DiffView, countChanges } from './DiffView';

const formatDate = (value) =>
  new Date(value).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' });

const authorName = (createdBy) => {
  if (!createdBy) return 'Système';
  const name = [createdBy.firstName, createdBy.lastName].filter(Boolean).join(' ');
  return name || createdBy.email || 'Système';
};

/**
 * Historique de contenu d'un document texte : versions, diff et restauration.
 */
function DocumentHistoryDialog({ open, onClose, documentId, documentName, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [current, setCurrent] = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedContent, setSelectedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('portalToken')}` });

  const fetchHistory = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError('');
    try {
      const [versionsRes, contentRes] = await Promise.all([
        fetch(`/api/documents/${documentId}/versions`, { headers: authHeaders() }),
        fetch(`/api/documents/${documentId}/content`, { headers: authHeaders() })
      ]);

      if (!versionsRes.ok) {
        const data = await versionsRes.json().catch(() => ({}));
        throw new Error(data.error || 'Historique indisponible');
      }

      const list = await versionsRes.json();
      setVersions(list);
      setCurrent(contentRes.ok ? (await contentRes.json()).content || '' : '');
      // La version la plus récente est déjà le contenu courant : on ouvre sur
      // la précédente, celle que l'on veut comparer dans la majorité des cas.
      setSelected(list.length > 1 ? list[1] : list[0] || null);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [documentId]);

  useEffect(() => {
    if (open) fetchHistory();
    else { setVersions([]); setSelected(null); setSelectedContent(''); setError(''); }
  }, [open, fetchHistory]);

  useEffect(() => {
    if (!selected || !documentId) { setSelectedContent(''); return; }
    let cancelled = false;
    (async () => {
      const res = await fetch(`/api/documents/${documentId}/versions/${selected.id}`, { headers: authHeaders() });
      if (res.ok && !cancelled) setSelectedContent((await res.json()).content || '');
    })();
    return () => { cancelled = true; };
  }, [selected, documentId]);

  const changeCount = useMemo(() => countChanges(selectedContent, current), [selectedContent, current]);

  const handleRestore = async () => {
    if (!selected) return;
    setRestoring(true);
    setError('');
    try {
      const res = await fetch(`/api/documents/${documentId}/versions/${selected.id}/restore`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Restauration impossible');
      if (onRestored) onRestored(data);
      await fetchHistory();
    } catch (err) {
      setError(err.message);
    }
    setRestoring(false);
  };

  const isCurrent = selected && versions.length > 0 && selected.id === versions[0].id;

  return (
    <Dialog
      open={open}
      onClose={() => !restoring && onClose()}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minHeight: '70vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <History size={20} color="#00d4aa" />
        Historique du document
        {documentName && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            - {documentName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', minHeight: '55vh' }}>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">
              Aucun historique pour ce document.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Liste des versions */}
            <Box sx={{ width: 290, borderRight: '1px solid rgba(255,255,255,0.06)', overflowY: 'auto' }}>
              <List dense disablePadding>
                {versions.map((version, index) => (
                  <ListItemButton
                    key={version.id}
                    selected={selected?.id === version.id}
                    onClick={() => setSelected(version)}
                    sx={{
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      display: 'block',
                      py: 1.25
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        v{version.version}
                      </Typography>
                      {index === 0 && (
                        <Chip label="actuelle" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {version.sourceLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <UserIcon size={11} />
                      {authorName(version.createdBy)} · {formatDate(version.createdAt)}
                    </Typography>
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {/* Diff avec le contenu actuel */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {isCurrent
                    ? `v${selected.version} — version actuelle`
                    : `v${selected?.version} comparée au contenu actuel`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isCurrent
                    ? 'C’est le contenu en place aujourd’hui.'
                    : `${changeCount} ligne(s) de différence · rouge = dans cette version, vert = actuellement`}
                </Typography>
              </Box>

              <DiffView before={selectedContent} after={current} contextLines={null} sx={{ flex: 1 }} />
            </Box>
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={restoring}>Fermer</Button>
        <Button
          variant="contained"
          startIcon={<RotateCcw size={16} />}
          onClick={handleRestore}
          disabled={restoring || !selected || isCurrent}
        >
          {restoring ? 'Restauration...' : `Restaurer v${selected?.version ?? ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DocumentHistoryDialog;
